import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { Box, Center, Spinner, Text, VStack } from '@chakra-ui/react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { currentUser, isLoading } = useAuth();
  const location = useLocation();
  const [retryCount, setRetryCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState<string>('');

  // Efectos para depuraciรณn - ayuda a diagnosticar problemas de autenticaciรณn
  useEffect(() => {
    if (isLoading) {
      setDebugInfo('Cargando informaciรณn de autenticaciรณn...');
    } else if (currentUser) {
      setDebugInfo(`Usuario autenticado: ${currentUser.email}`);
      console.log('ProtectedRoute - Usuario autenticado:', currentUser);
    } else {
      setDebugInfo('No hay usuario autenticado');
      console.log('ProtectedRoute - Sin usuario autenticado, redirigiendo a login');
    }
  }, [isLoading, currentUser]);

  // Intentar varias veces si estรก cargando (mรกs tolerante)
  useEffect(() => {
    let timer: number | undefined;
    if (isLoading && retryCount < 10) { // Aumentado a 10 intentos
      timer = window.setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setDebugInfo(`Reintentando verificaciรณn de autenticaciรณn (${retryCount + 1}/10)...`);
      }, 500); // Reducido a 500ms para ser mรกs rรกpido
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, retryCount]);

  // Si despuรฉs de varios intentos sigue cargando, verificar localStorage manualmente
  useEffect(() => {
    if (retryCount >= 10 && isLoading) {
      try {
        // Verificaciรณn alternativa del localStorage
        const storedUser = localStorage.getItem('currentUser');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser && storedToken) {
          try {
            const tokenData = JSON.parse(atob(storedToken));
            const currentTime = Date.now();
            
            // Si el token no estรก completamente expirado, permitir acceso
            if (tokenData.exp > (currentTime - 15 * 60 * 1000)) { // 15 minutos de gracia
              console.log('ProtectedRoute - Respaldo: Usuario encontrado en localStorage con token vรกlido');
              setDebugInfo('Autenticaciรณn desde localStorage (modo respaldo)');
              return;
            }
          } catch (tokenError) {
            console.error('Error al verificar token de respaldo:', tokenError);
          }
        }
      } catch (error) {
        console.error('Error al verificar localStorage:', error);
      }
    }
  }, [retryCount, isLoading]);

  // Mostrar spinner mientras se verifica la autenticaciรณn (mรกs tiempo)
  if (isLoading && retryCount < 10) {
    return (
      <Box minH="100vh">
        <Center h="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="red.500" />
            <Text color="gray.600">{debugInfo}</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Verificaciรณn manual de localStorage si la autenticaciรณn falla (mรกs permisiva)
  if (!currentUser && retryCount >= 10) {
    try {
      const storedUser = localStorage.getItem('currentUser');
      const storedToken = localStorage.getItem('authToken');
      
      if (storedUser && storedToken) {
        try {
          const tokenData = JSON.parse(atob(storedToken));
          const currentTime = Date.now();
          
          // Ser muy permisivo con tokens en localStorage
          if (tokenData.exp > (currentTime - 30 * 60 * 1000)) { // 30 minutos de gracia
            console.log('ProtectedRoute - Autenticaciรณn de respaldo: Permitiendo acceso con datos de localStorage');
            return <>{children}</>;
          }
        } catch (tokenError) {
          console.error('Error al verificar token de respaldo:', tokenError);
          // Incluso si hay error con el token, si hay usuario almacenado, permitir acceso
          if (storedUser) {
            console.log('ProtectedRoute - Autenticaciรณn de emergencia: Permitiendo acceso solo con usuario almacenado');
            return <>{children}</>;
          }
        }
      }
    } catch (error) {
      console.error('Error en autenticaciรณn de respaldo:', error);
    }
  }

  // Verificación final muy permisiva antes de redirigir
  if (!currentUser) {
    // Última verificación de localStorage antes de redirigir
    const storedUser = localStorage.getItem('currentUser');
    const storedToken = localStorage.getItem('authToken');
    
    if (storedUser && storedToken) {
      try {
        const tokenData = JSON.parse(atob(storedToken));
        const currentTime = Date.now();
        
        // Ser EXTREMADAMENTE permisivo - solo redirigir si el token está muy expirado
        if (tokenData.exp > (currentTime - 2 * 60 * 60 * 1000)) { // 2 horas de gracia
          console.log('ProtectedRoute - ÚLTIMO RECURSO: Permitiendo acceso con datos de localStorage');
          return <>{children}</>;
        }
      } catch (error) {
        console.error('Error en verificación final:', error);
        // Incluso si hay error, si hay datos de usuario, permitir acceso
        if (storedUser) {
          console.log('ProtectedRoute - EMERGENCIA: Permitiendo acceso solo con usuario almacenado');
          return <>{children}</>;
        }
      }
    }
    
    // Solo redirigir si realmente no hay NADA
    console.log('ProtectedRoute - REDIRIGIENDO: No hay datos de autenticación válidos');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si el usuario está autenticado, mostrar el contenido protegido
  return <>{children}</>;
};
